import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * ShareClanModal — a creator's one-tap "announce my Clan" surface.
 *
 * Renders:
 *   · Live preview of the per-Clan OG card (fetched from
 *     /api/og/creator/{handle}.png) so the creator sees the exact card
 *     WhatsApp / Telegram / X will render for anyone who follows the link.
 *   · Editable message textarea with a smart default template.
 *   · Six share targets — WhatsApp, X, Telegram, Facebook, native OS sheet,
 *     plus Copy Link.
 *
 * The URL shared is `${origin}/api/share/creator/{handle}`, which returns
 * an HTML page carrying per-Clan OG meta and meta-refreshes real users to
 * the SPA `/creator/{handle}` route.
 */
export default function ShareClanModal({ open, onClose, handle: handleProp }) {
  const { user } = useAuth();
  const handle = handleProp || user?.handle;

  const shareUrl = useMemo(
    () => (handle ? `${window.location.origin}/api/share/creator/${handle}` : ''),
    [handle],
  );

  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  // Cache-buster so hitting the modal after minting a fresh Bit reloads
  // the preview card instead of the stale cached one.
  const [previewTs, setPreviewTs] = useState(() => Date.now());

  useEffect(() => {
    if (!open) return;
    setMessage("My Clan is live on Clanster, 🚀 Grab a Bit to join my inner circle");
    setCopied(false);
    setPreviewTs(Date.now());
  }, [open, handle, user?.display_name]);

  if (!open) return null;

  const previewSrc = `${window.location.origin}/api/og/creator/${handle}.png?_ts=${previewTs}`;
  const fullText = `${message}\n\n${shareUrl}`;

  const openWindow = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer,width=680,height=560');
  };

  const shareWhatsApp = () => openWindow(
    `https://wa.me/?text=${encodeURIComponent(fullText)}`,
  );
  const shareX = () => openWindow(
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(shareUrl)}`,
  );
  const shareTelegram = () => openWindow(
    `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(message)}`,
  );
  const shareFacebook = () => openWindow(
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(message)}`,
  );
  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'My Clan on Clanster', text: message, url: shareUrl });
      } catch {
        // user cancelled — no-op
      }
    }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard denied → surface the URL for manual copy
      window.prompt('Copy this link:', fullText);
    }
  };

  return (
    <>
      <div className="scm-scrim" onClick={onClose} data-testid="share-clan-scrim" />
      <div className="scm-sheet" role="dialog" aria-modal="true" data-testid="share-clan-modal">
        <div className="scm-head">
          <div>
            <div className="scm-title">Share your Clan</div>
            <div className="scm-sub">This is exactly how the card will render on WhatsApp, X and other chat apps.</div>
          </div>
          <button
            type="button"
            className="scm-close"
            onClick={onClose}
            aria-label="Close"
            data-testid="share-clan-close"
          >×</button>
        </div>

        <div className="scm-preview" data-testid="share-clan-preview">
          <img src={previewSrc} alt="Live preview of your Clan share card" loading="eager" />
        </div>

        <label className="scm-msg-label" htmlFor="scm-msg">Your message</label>
        <textarea
          id="scm-msg"
          className="scm-msg"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          maxLength={240}
          data-testid="share-clan-message"
        />
        <div className="scm-msg-count">{message.length}/240</div>

        <div className="scm-actions">
          <button type="button" className="scm-btn scm-btn-wa" onClick={shareWhatsApp} data-testid="share-clan-whatsapp">
            <span className="scm-btn-icon" aria-hidden="true">💬</span> WhatsApp
          </button>
          <button type="button" className="scm-btn scm-btn-x" onClick={shareX} data-testid="share-clan-x">
            <span className="scm-btn-icon" aria-hidden="true">𝕏</span> X (Twitter)
          </button>
          <button type="button" className="scm-btn scm-btn-tg" onClick={shareTelegram} data-testid="share-clan-telegram">
            <span className="scm-btn-icon" aria-hidden="true">✈️</span> Telegram
          </button>
          <button type="button" className="scm-btn scm-btn-fb" onClick={shareFacebook} data-testid="share-clan-facebook">
            <span className="scm-btn-icon" aria-hidden="true">f</span> Facebook
          </button>
          {typeof navigator !== 'undefined' && navigator.share && (
            <button type="button" className="scm-btn scm-btn-native" onClick={shareNative} data-testid="share-clan-native">
              <span className="scm-btn-icon" aria-hidden="true">↗</span> More…
            </button>
          )}
          <button
            type="button"
            className={`scm-btn scm-btn-copy${copied ? ' is-copied' : ''}`}
            onClick={copyLink}
            data-testid="share-clan-copy"
          >
            <span className="scm-btn-icon" aria-hidden="true">{copied ? '✓' : '🔗'}</span>
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>

        <div className="scm-url" title={shareUrl}>{shareUrl}</div>
      </div>
    </>
  );
}
